import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import type { TemplatePrompt } from '@intone/shared/types';

import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

interface TemplateBlockProps {
  template: Record<string, TemplatePrompt>
  onUpdate: (template: Record<string, TemplatePrompt>) => void
}

export function TemplateBlock({ template, onUpdate}: TemplateBlockProps) {
  // Track which keys have duplicate name errors (key -> attempted duplicate name)
  const [duplicateErrors, setDuplicateErrors] = useState<Record<string, string>>({})

  const handleAddPrompt = () => {
    let n = 1
    while (`prompt_${n}` in template) n++

    onUpdate({
      [`prompt_${n}`]: { text: '', visible: true },
      ...template,
    })
  }

  const handleUpdatePrompt = (key: string, text: string) => {
    onUpdate({
      ...template,
      [key]: { ...template[key], text },
    })
  }

  const handleRenamePrompt = (oldKey: string, newKey: string) => {
    // Clear any previous error for this key
    if (duplicateErrors[oldKey]) {
      setDuplicateErrors((prev) => {
        const next = { ...prev }
        delete next[oldKey]
        return next
      })
    }

    if (oldKey === newKey || !newKey.trim()) return

    // Prevent overwriting an existing prompt with a different name
    if (newKey in template && newKey !== oldKey) {
      setDuplicateErrors((prev) => ({ ...prev, [oldKey]: newKey }))
      return
    }

    const newTemplate: Record<string, TemplatePrompt> = {}
    for (const [key, value] of Object.entries(template)) {
      if (key === oldKey) {
        newTemplate[newKey] = value
      } else {
        newTemplate[key] = value
      }
    }
    onUpdate(newTemplate)
  }

  const handleRemovePrompt = (key: string) => {
    const newTemplate = { ...template }
    delete newTemplate[key]
    onUpdate(newTemplate)
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <h3 className="text-heading font-semibold">Template Prompts</h3>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddPrompt}
          className="h-7"
        >
          Add Prompt
        </Button>
      </div>

      {Object.entries(template).length === 0 && (
        <div className="text-center py-8 text-gray-500 text-detail border-2 border-dashed rounded-lg">
          No prompts added. Click "Add Prompt" to create one.
        </div>
      )}

      <div className="space-y-3">
        {Object.entries(template).map(([key, value]) => (
          <Card key={key} className="border-gray-300">
            <CardHeader className="bg-gray-50 border-b border-gray-300 py-2 flex-row gap-2 space-y-0">
              <div className="flex-1">
                <Input
                  value={key}
                  onChange={(e) => handleRenamePrompt(key, e.target.value)}
                  className={`h-7 font-semibold border-dashed ${
                    duplicateErrors[key] ? 'border-destructive focus-visible:ring-destructive' : ''
                  }`}
                  placeholder="Prompt name"
                  name={key}
                />
                {duplicateErrors[key] ? (
                  <p className="text-detail text-destructive mt-1">
                    A prompt named "{duplicateErrors[key]}" already exists
                  </p>
                ) : (
                  <p className="text-detail text-gray-500 mt-1">
                    Use <code className="bg-gray-100 px-1 rounded">transition_to_{key}</code>{' '}
                    tool to move to this state.
                  </p>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleRemovePrompt(key)}
                className="h-7 w-7 p-0 text-destructive hover:text-white hover:bg-destructive border-red-200 hover:border-destructive"
                title="Delete"
              >
                <Trash2 className="w-5 h-5"/>
              </Button>
            </CardHeader>
            <CardContent className="pt-6">
              <Textarea
                value={value.text || ''}
                onChange={(e) => handleUpdatePrompt(key, e.target.value)}
                placeholder={`Enter prompt for ${key}`}
                className="min-h-[150px] font-mono"
              />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}